import { Autocomplete, Chip, CircularProgress, Grid, InputAdornment, TextField } from '@mui/material';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ZONE_LIST } from 'src/helpers';
import { useGetContentTypesQuery } from 'src/redux/services/ContentTypeServices';
import { onPropertiesChange } from 'src/redux/slices/courseSlice';
import { getCategoryList, getTagsList } from 'src/services';

export const CourseProperties = ({ properties, setProperties, error }) => {
  const dispatch = useDispatch();

  const [categoryList, setCategoryList] = useState([]);
  const [tagsList, setTagsList] = useState([]);

  const [categoryOpen, setCategoryOpen] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [contentTypesOpen, setContentTypesOpen] = useState(false);

  const [categoryLoading, setCategoryLoading] = useState(false);
  const [tagsLoading, setTagsLoading] = useState(false);

  const { data: contentTypesData, isFetching: contentTypesLoading } = useGetContentTypesQuery(
    {},
    { skip: !contentTypesOpen }
  );

  const { title, duration, category, tags, contentType, description, zone } = properties;
  const contentTypesList = contentTypesData?.contentTypes || [];

  const getCategory = async () => {
    setCategoryLoading(true);
    try {
      const response = await getCategoryList();
      setCategoryList(response.categories);
      setCategoryLoading(false);
    } catch (error) {
      setCategoryLoading(false);
      console.log(error);
    }
  };
  const getTags = async () => {
    setTagsLoading(true);
    try {
      const response = await getTagsList();
      setTagsList(response.tags);
      setTagsLoading(false);
    } catch (error) {
      console.log(error);
      setTagsLoading(false);
    }
  };

  const tagOptions = useMemo(() => {
    return tagsList.filter((tag) => !tags.some((selectedTag) => selectedTag._id === tag._id));
  }, [tagsList, tags]);

  return (
    <Grid container spacing={3} sx={{ my: 'auto' }}>
      <Grid item xs={12}>
        <TextField
          label="عنوان دوره"
          value={title}
          onChange={(e) => {
            dispatch(onPropertiesChange({ title: e.target.value }));
            setProperties({ ...properties, title: e.target.value });
          }}
          fullWidth
          error={error && title === ''}
          autoComplete="off"
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          options={categoryList}
          getOptionLabel={(option) => `${option?.parent?.name || ''} ${option.name ? '/' : ''} ${option?.name || ''}`}
          value={category}
          onChange={(e, newValue) => {
            setProperties({ ...properties, category: newValue });
          }}
          open={categoryOpen}
          onOpen={() => {
            getCategory();
            setCategoryOpen(true);
          }}
          onClose={() => setCategoryOpen(false)}
          loading={categoryLoading}
          loadingText={<CircularProgress />}
          renderInput={(params) => (
            <TextField
              {...params}
              label="دسته بندی"
              error={error && (category._id === undefined || category._id === '')}
            />
          )}
          isOptionEqualToValue={(option) => option._id === category._id}
          disableClearable
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="توضیحات"
          value={description}
          onChange={(e) => setProperties({ ...properties, description: e.target.value })}
          fullWidth
          multiline
          minRows={5}
          error={error && description === ''}
          autoComplete="off"
        />
      </Grid>
      <Grid item xs={6}>
        <Autocomplete
          options={contentTypesList}
          getOptionLabel={(option) => option.name}
          open={contentTypesOpen}
          onOpen={() => {
            setContentTypesOpen(true);
          }}
          onClose={() => setContentTypesOpen(false)}
          loading={contentTypesLoading}
          loadingText={<CircularProgress />}
          value={contentType}
          onChange={(e, newValue) => {
            setProperties({ ...properties, contentType: newValue });
          }}
          renderInput={(params) => <TextField {...params} label="نوع محتوا" error={error && contentType === ''} />}
          disableClearable
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          label="مدت دوره"
          value={duration}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '') {
              setProperties({ ...properties, duration: '' });
              return;
            }
            const numValue = Number(value);
            if (numValue < 1) return;
            setProperties({ ...properties, duration: numValue });
          }}
          onKeyDown={(e) => {
            if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
              e.preventDefault();
            }
          }}
          fullWidth
          type="number"
          error={error && (duration === 0 || duration === '')}
          InputProps={{
            endAdornment: <InputAdornment position="end">دقیقه</InputAdornment>,
            inputProps: { min: 1 },
          }}
          autoComplete="off"
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          multiple
          options={tagOptions}
          getOptionLabel={(option) => option.name || ''}
          open={tagsOpen}
          onOpen={() => {
            getTags();
            setTagsOpen(true);
          }}
          onClose={() => setTagsOpen(false)}
          loading={tagsLoading}
          loadingText={<CircularProgress />}
          value={tags}
          onChange={(e, newValue) => {
            setProperties({ ...properties, tags: [...new Set(newValue)] });
          }}
          renderInput={(params) => <TextField {...params} label="برچسب ها" error={error && tags.length === 0} />}
          renderTags={(value, getTagProps) =>
            [...new Set(value)].map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return <Chip key={key} variant="outlined" label={option.name} color="primary" size="medium" {...tagProps} />;
            })
          }
          disableClearable
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          options={ZONE_LIST}
          getOptionLabel={(option) => option.label}
          value={zone}
          freeSolo
          onChange={(event, newValue) => {
            setProperties({ ...properties, zone: newValue });
          }}
          renderInput={(params) => <TextField {...params} label="زون ها" error={error && !zone?.value} />}
          renderTags={(value, getTagProps) =>
            [...new Set(value)].map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  variant="outlined"
                  label={option?.label}
                  color="primary"
                  size="medium"
                  {...tagProps}
                />
              );
            })
          }
          // value={zone}
        />
        {/* <Autocomplete
          // multiple
          options={ZONE_LIST}
          getOptionLabel={(option) => option.label || ''}
          value={zone}
          onChange={(e, newValue) => {
            console.log(newValue);
            setProperties({ ...properties, zone: [...new Set(newValue)] });
          }}
          freeSolo
          renderInput={(params) => <TextField {...params} label="زون ها" error={error && zone.length === 0} />}
          // renderTags={(value, getTagProps) =>
          //   [...new Set(value)].map((option, index) => (
          //     <Chip variant="outlined" label={option?.name} color="primary" size="medium" {...getTagProps({ index })} />
          //   ))
          // }
          disableClearable
          filterSelectedOptions
        /> */}
      </Grid>
    </Grid>
  );
};
